import { translate } from "../i18n/translate";
import { DateLocalizationHelper } from "../common/DateLocalizationHelper";

export interface DateRangePickerOptions {
    selectedStartDate?: Date | null;
    selectedEndDate?: Date | null;
    onApply: (startDate: Date | null, endDate: Date | null) => void;
    onCancel: () => void;
}

export class DateRangePicker {
    private readonly container: HTMLElement;
    private readonly options: DateRangePickerOptions;
    private selectedStartDate: Date | null;
    private selectedEndDate: Date | null;
    private readonly dateHelper: DateLocalizationHelper;
    private currentLeftMonth: number;
    private currentLeftYear: number;
    private currentRightMonth: number;
    private currentRightYear: number;

    constructor(container: HTMLElement, options: DateRangePickerOptions) {
        this.container = container;
        this.options = options;
        this.selectedStartDate = options.selectedStartDate || null;
        this.selectedEndDate = options.selectedEndDate || null;
        this.dateHelper = DateLocalizationHelper.getInstance();
        
        // Initialize current months/years - if we have selected dates, start from the start date's month
        if (this.selectedStartDate) {
            this.currentLeftMonth = this.selectedStartDate.getMonth();
            this.currentLeftYear = this.selectedStartDate.getFullYear();
        } else {
            const today = new Date();
            this.currentLeftMonth = today.getMonth();
            this.currentLeftYear = today.getFullYear();
        }
        
        this.currentRightMonth = this.currentLeftMonth === 11 ? 0 : this.currentLeftMonth + 1;
        this.currentRightYear = this.currentLeftMonth === 11 ? this.currentLeftYear + 1 : this.currentLeftYear;
    }

    public render(): void {
        this.container.innerHTML = `
            <div class="daterangepicker ${this.dateHelper.getDatePickerDirection()} show-calendar opensright" style="top: auto; left: auto; right: auto; display: block; position: relative;">
                <div class="ranges"></div>
                <div class="drp-calendar left">
                    ${this.generateCalendarTable(this.currentLeftMonth, this.currentLeftYear, 'left')}
                </div>
                <div class="drp-calendar right">
                    ${this.generateCalendarTable(this.currentRightMonth, this.currentRightYear, 'right')}
                </div>
                <div class="drp-buttons">
                    <span class="drp-selected" id="selected-range">${this.getSelectedRangeText()}</span>
                    <button class="cancelBtn btn btn-sm btn-link" type="button">${translate('CalendarEvents.Cancel')}</button>
                    <button class="applyBtn btn btn-sm btn-primary" type="button">${translate('Hero.Apply')}</button>
                </div>
            </div>
        `;
        
        this.initializeEventListeners();
    }

    private generateCalendarTable(month: number, year: number, position: 'left' | 'right'): string {
        const monthNames = this.dateHelper.getShortMonthNames();
        const daysOfWeek = this.dateHelper.getMinimalWeekdayNames();
        const firstDayOfWeek = this.dateHelper.getFirstDayOfWeek();
        
        // Reorganize days of week array to start with the locale's first day
        const reorderedDaysOfWeek = [
            ...daysOfWeek.slice(firstDayOfWeek),
            ...daysOfWeek.slice(0, firstDayOfWeek)
        ];
        
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        
        // Adjust start day of week based on locale's first day
        let startDayOfWeek = (firstDay.getDay() - firstDayOfWeek + 7) % 7;
        
        const today = this.dateHelper.getToday();
        const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;
        const todayDate = today.getDate();
        
        let tableHTML = `
            <div class="calendar-table">
                <table class="table-condensed">
                    <thead>
                        <tr>
                            ${position === 'left' ? '<th class="prev available"><span></span></th>' : '<th></th>'}
                            <th colspan="5" class="month">${monthNames[month]} ${year}</th>
                            ${position === 'right' ? '<th class="next available"><span></span></th>' : '<th></th>'}
                        </tr>
                        <tr>
                            ${reorderedDaysOfWeek.map(day => `<th>${day}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
        `;
        
        // Generate calendar days
        let currentDate = 1;
        let nextMonthDate = 1;
        
        // Previous month's trailing days
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevYear = month === 0 ? year - 1 : year;
        const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();
        
        for (let week = 0; week < 6; week++) {
            tableHTML += '<tr>';
            
            for (let day = 0; day < 7; day++) {
                const cellIndex = week * 7 + day;
                
                if (week === 0 && day < startDayOfWeek) {
                    // Previous month days
                    const prevDate = prevMonthLastDay - (startDayOfWeek - day - 1);
                    const dayOfWeekIndex = (day + firstDayOfWeek) % 7;
                    const isWeekend = this.isWeekend(dayOfWeekIndex) ? ' weekend' : '';
                    tableHTML += `<td class="${isWeekend} off available" data-title="r${week}c${day}">${prevDate}</td>`;
                } else if (currentDate <= daysInMonth) {
                    // Current month days
                    const dayOfWeekIndex = (day + firstDayOfWeek) % 7;
                    const isWeekend = this.isWeekend(dayOfWeekIndex) ? ' weekend' : '';
                    const currentDateObj = new Date(year, month, currentDate);
                    let cellClasses = `${isWeekend} available`;
                    
                    // Check if this is today
                    if (isCurrentMonth && currentDate === todayDate) {
                        cellClasses += ' today';
                    }
                    
                    // Check if this matches our selected dates
                    if (this.selectedStartDate && this.dateHelper.isSameDate(currentDateObj, this.selectedStartDate)) {
                        cellClasses += ' start-date active';
                        if (this.selectedEndDate && this.dateHelper.isSameDate(this.selectedStartDate, this.selectedEndDate)) {
                            cellClasses += ' end-date';
                        }
                    } else if (this.selectedEndDate && this.dateHelper.isSameDate(currentDateObj, this.selectedEndDate)) {
                        cellClasses += ' end-date active';
                    } else if (this.selectedStartDate && this.selectedEndDate && 
                               this.dateHelper.isDateInRange(currentDateObj, this.selectedStartDate, this.selectedEndDate)) {
                        cellClasses += ' in-range';
                    }
                    
                    tableHTML += `<td class="${cellClasses}" data-title="r${week}c${day}" data-date="${year}-${String(month + 1).padStart(2, '0')}-${String(currentDate).padStart(2, '0')}">${currentDate}</td>`;
                    currentDate++;
                } else {
                    // Next month days
                    const dayOfWeekIndex = (day + firstDayOfWeek) % 7;
                    const isWeekend = this.isWeekend(dayOfWeekIndex) ? ' weekend' : '';
                    tableHTML += `<td class="${isWeekend} off available" data-title="r${week}c${day}">${nextMonthDate}</td>`;
                    nextMonthDate++;
                }
            }
            
            tableHTML += '</tr>';
            
            // Break if we've shown all days of the month and filled the week
            if (currentDate > daysInMonth && nextMonthDate > 7) break;
        }
        
        tableHTML += `
                    </tbody>
                </table>
            </div>
            <div class="calendar-time" style="display: none;"></div>
        `;
        
        return tableHTML;
    }

    private initializeEventListeners(): void {
        let startDate: Date | null = this.selectedStartDate;
        let endDate: Date | null = this.selectedEndDate;
        const selectedRangeSpan = this.container.querySelector('#selected-range') as HTMLElement;
        const applyButton = this.container.querySelector('.applyBtn') as HTMLButtonElement;
        const cancelButton = this.container.querySelector('.cancelBtn') as HTMLButtonElement;
        
        // Update display if we have saved dates
        if (startDate && endDate) {
            selectedRangeSpan.textContent = this.dateHelper.formatDateRange(startDate, endDate);
        } else if (startDate) {
            selectedRangeSpan.textContent = this.dateHelper.formatDate(startDate);
        }
        
        // Handle date cell clicks
        const dateCells = this.container.querySelectorAll('td.available:not(.off)');
        dateCells.forEach(cell => {
            cell.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                const dateStr = (cell as HTMLElement).getAttribute('data-date');
                if (!dateStr) return;
                
                // Parse date more reliably using the helper
                const clickedDate = this.dateHelper.parseDate(dateStr, 'YYYY-MM-DD');
                
                // Validate the date
                if (!clickedDate) {
                    console.error('Invalid date created from:', dateStr);
                    return;
                }
                
                // Clear previous selections
                dateCells.forEach(c => {
                    c.classList.remove('start-date', 'end-date', 'in-range', 'active');
                });
                
                if (!startDate || (startDate && endDate)) {
                    // Starting new selection
                    startDate = clickedDate;
                    endDate = null;
                    cell.classList.add('start-date', 'active');
                    selectedRangeSpan.textContent = this.formatDate(startDate);
                } else if (startDate && !endDate) {
                    // Completing selection
                    if (clickedDate < startDate) {
                        // Swap dates if end date is before start date
                        endDate = startDate;
                        startDate = clickedDate;
                    } else {
                        endDate = clickedDate;
                    }
                    
                    // Mark start and end dates
                    this.updateDateSelection(startDate, endDate);
                    selectedRangeSpan.textContent = this.dateHelper.formatDateRange(startDate, endDate);
                }
            });
        });
        
        // Handle navigation buttons
        const leftCalendar = this.container.querySelector('.drp-calendar.left');
        const rightCalendar = this.container.querySelector('.drp-calendar.right');
        
        // Handle prev button (in left calendar)
        const prevButton = leftCalendar?.querySelector('.prev.available');
        if (prevButton) {
            prevButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.navigateMonth(-1);
            });
        }
        
        // Handle next button (in right calendar)
        const nextButton = rightCalendar?.querySelector('.next.available');
        if (nextButton) {
            nextButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.navigateMonth(1);
            });
        }
        
        // Handle apply and cancel buttons
        applyButton?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Validate dates before applying
            if (this.dateHelper.isValidDate(startDate)) {
                this.selectedStartDate = startDate;
                this.selectedEndDate = this.dateHelper.isValidDate(endDate) ? endDate : null;
                this.options.onApply(this.selectedStartDate, this.selectedEndDate);
            } else {
                console.error('Cannot apply invalid date selection');
            }
        });
        
        cancelButton?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.options.onCancel();
        });
    }

    private updateDateSelection(startDate: Date, endDate: Date): void {
        const dateCells = this.container.querySelectorAll('td.available:not(.off)');
        
        dateCells.forEach(cell => {
            const dateStr = (cell as HTMLElement).getAttribute('data-date');
            if (!dateStr) return;
            
            // Parse date using the helper for consistency
            const cellDate = this.dateHelper.parseDate(dateStr, 'YYYY-MM-DD');
            
            if (!cellDate) {
                console.error('Invalid date in updateDateSelection:', dateStr);
                return;
            }
            
            if (cellDate.getTime() === startDate.getTime()) {
                cell.classList.add('start-date', 'active');
            } else if (cellDate.getTime() === endDate.getTime()) {
                cell.classList.add('end-date', 'active');
            } else if (cellDate > startDate && cellDate < endDate) {
                cell.classList.add('in-range');
            }
        });
    }

    private navigateMonth(direction: number): void {
        // direction: -1 for previous month, +1 for next month
        // Always update the left calendar first, then the right will follow
        if (direction === -1) {
            // Navigate to previous month
            if (this.currentLeftMonth === 0) {
                this.currentLeftMonth = 11;
                this.currentLeftYear--;
            } else {
                this.currentLeftMonth--;
            }
        } else {
            // Navigate to next month
            if (this.currentLeftMonth === 11) {
                this.currentLeftMonth = 0;
                this.currentLeftYear++;
            } else {
                this.currentLeftMonth++;
            }
        }
        
        // Update the right calendar to maintain the two-month view
        this.updateAdjacentMonth();
        
        // Re-render the calendar
        this.render();
    }

    private updateAdjacentMonth(): void {
        if (this.currentLeftMonth === 11) {
            this.currentRightMonth = 0;
            this.currentRightYear = this.currentLeftYear + 1;
        } else {
            this.currentRightMonth = this.currentLeftMonth + 1;
            this.currentRightYear = this.currentLeftYear;
        }
    }

    private formatDate(date: Date): string {
        return this.dateHelper.formatDate(date);
    }

    private isWeekend(dayOfWeek: number): boolean {
        // In most locales, Saturday (6) and Sunday (0) are weekends
        // However, some locales may have different weekend days
        // For now, we'll use the standard Saturday/Sunday weekend
        return dayOfWeek === 0 || dayOfWeek === 6;
    }

    private getSelectedRangeText(): string {
        if (this.selectedStartDate && this.selectedEndDate) {
            return this.dateHelper.formatDateRange(this.selectedStartDate, this.selectedEndDate);
        } else if (this.selectedStartDate) {
            return this.dateHelper.formatDate(this.selectedStartDate);
        } else {
            const today = this.dateHelper.getToday();
            return this.dateHelper.formatDateRange(today, today);
        }
    }

    public navigateToMonth(month: number, year: number): void {
        this.currentLeftMonth = month;
        this.currentLeftYear = year;
        this.updateAdjacentMonth();
        this.render();
    }

    public getCurrentDisplayedMonths(): { left: { month: number, year: number }, right: { month: number, year: number } } {
        return {
            left: { month: this.currentLeftMonth, year: this.currentLeftYear },
            right: { month: this.currentRightMonth, year: this.currentRightYear }
        };
    }

    public destroy(): void {
        this.container.innerHTML = '';
    }

    public updateLocale(locale?: string): void {
        if (locale) {
            this.dateHelper.setLocale(locale);
        }
        // Re-render the component with new locale
        this.render();
    }
}
